/**
 * The quick-capture buffer: notes written while the network was gone.
 *
 * This is a **deliberate exception** to the invariant every other part of the
 * front end keeps — that nothing belonging to an account survives on the device
 * (D260815d). The service worker caches nothing under `/api/`, signing out
 * clears the query cache, and `OfflineNotice` says so in words. Here, a note is
 * written to `localStorage` and stays there until it has been sent.
 *
 * The exception is bought with three rules, and the first is the one that pays
 * for it:
 *
 * 1. **Signing out empties the buffer.** `useLogout` already clears the query
 *    cache for exactly this reason; without the same call here, one account's
 *    unsent note would be replayed into the next account signed in on the same
 *    phone.
 * 2. Every entry carries the account it was written for, and the replay checks
 *    it against the session before sending — a second guard for the same
 *    failure, because a buffer that outlives its account is worse than no
 *    buffer.
 * 3. Reading and writing happen inside `try`, exactly as in `lib/i18n/locale.ts`:
 *    `localStorage` **throws** rather than returning null when storage is
 *    denied, and a refusal must cost the buffer, never the render.
 */

const STORAGE_KEY = 'kanto:outbox';

export interface OutboxNote {
  /** Local to this device. Only ever used to take the entry back out again. */
  id: string;
  /** Whose note this is. Checked against the session before it is ever sent. */
  username: string;
  body: string;
  writtenAt: string;
}

/**
 * Everything waiting, or nothing at all.
 *
 * Anything that does not read back as a list of entries is discarded rather than
 * repaired: the value is written by this module alone, so a shape that surprises
 * it is a value some other version wrote, and guessing at it would replay
 * something nobody can see.
 */
export function readOutbox(): OutboxNote[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isEntry) : [];
  } catch {
    return [];
  }
}

/** What is waiting for one account, which is the only thing worth replaying. */
export function outboxFor(username: string): OutboxNote[] {
  return readOutbox().filter((entry) => entry.username === username);
}

/** Adds a note to the buffer and answers with the entry that was written. */
export function queueNote(username: string, body: string): OutboxNote {
  const entry: OutboxNote = { id: localId(), username, body, writtenAt: new Date().toISOString() };
  write([...readOutbox(), entry]);
  return entry;
}

/** Takes one entry out — called once the server has acknowledged it. */
export function forgetNote(id: string): void {
  write(readOutbox().filter((entry) => entry.id !== id));
}

/**
 * Empties the buffer entirely.
 *
 * Called on the way out of a session. Not "empties this account's entries": a
 * device that has just been signed out of should hold nothing of anybody's, and
 * a filter is one condition away from leaving something behind.
 */
export function clearOutbox(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Denied storage cannot be holding anything either.
  }
}

function write(entries: OutboxNote[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Quota exceeded, or writing denied. The note is lost rather than the
    // render — which is why the interface says a capture is waiting only once
    // this has succeeded.
  }
}

function isEntry(value: unknown): value is OutboxNote {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.id === 'string' &&
    typeof entry.username === 'string' &&
    typeof entry.body === 'string' &&
    typeof entry.writtenAt === 'string'
  );
}

/**
 * An identifier for this device alone.
 *
 * Not `crypto.randomUUID()`: that is undefined outside a secure context, and an
 * instance reached over plain `http://` on a home network is exactly where this
 * buffer earns its place. Nothing here is a secret — it names a row in a list
 * only this browser can read.
 */
function localId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
